import { FC, useEffect } from "react";

import { formatCurrencyAmount, getDollarValue, getIsoLocaleFromLocale, Locales } from "@simplyk/common";
import { useFormContext } from "react-hook-form";

import { useTranslationLocales } from "../../../Context/TranslationLocaleContext";
import { getTranslationLabelLanguage } from "../../../helpers/loadTranslationLabels";
import { TranslatedRichTextField } from "../../../sharedComponents/TranslatedRichTextField";
import { TranslatedTextField } from "../../../sharedComponents/TranslatedTextField";
import { TranslationCard } from "../../../sharedComponents/TranslationCard";
import { useTranslationSwapVersion } from "../../../sharedComponents/TranslationSwapContext";
import { TicketingTranslationsData } from "../../../translation.types";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { isRequired } from "@/common/helpers/validators";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardRichTextField } from "@/dashboard-design-system/RichTextField/DashboardRichTextField";
import { DashboardSkeleton } from "@/dashboard-design-system/Skeleton/DashboardSkeleton";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";

export interface PrizeTexts {
  title: string;
  description: string | null;
}

interface PrizeTranslationCardProps {
  index: number;
  ticketingPrizeId: string;
  value: number | null;
  savedTexts?: PrizeTexts;
  textsToTranslate?: PrizeTexts;
  translationLocale: Locales;
  isTranslationColumn: boolean;
  isLoading?: boolean;
}

export const PrizeTranslationCard: FC<PrizeTranslationCardProps> = ({
  index,
  ticketingPrizeId,
  value,
  savedTexts,
  textsToTranslate,
  translationLocale,
  isTranslationColumn,
  isLoading = false,
}) => {
  const { t } = useTranslate();
  const { organizationCurrency } = useCurrentUserContext();
  const { isoLocale } = useLocaleContext();
  const { control, register, setValue, getValues, formState } = useFormContext<TicketingTranslationsData>();
  const swapVersion = useTranslationSwapVersion();
  const locales = useTranslationLocales();
  const column = isTranslationColumn ? "translations" : "fields";
  const fieldLocale = isTranslationColumn ? translationLocale : locales?.formLocale;
  const labelLanguage = getTranslationLabelLanguage(fieldLocale);

  const titleName = `${column}.prizesFields.${index}.title` as const;
  const descriptionName = `${column}.prizesFields.${index}.description` as const;
  const titleError = formState.errors[column]?.prizesFields?.[index]?.title?.message;

  useEffect(() => {
    if (getValues(descriptionName) === undefined) {
      setValue(descriptionName, savedTexts?.description ?? "");
    }
  }, [descriptionName, getValues, savedTexts?.description, setValue]);

  const descriptionDefaultValue =
    swapVersion > 0 ? getValues(descriptionName) : getValues(descriptionName) || savedTexts?.description || "";
  const titleLabel = t("dashboard", "liveFormEditor.stepPrizes.prizeTitle", { lng: labelLanguage });
  const descriptionLabel = t("dashboard", "ticketing.rateDescription", { lng: labelLanguage });
  const prizeName =
    savedTexts?.title ||
    textsToTranslate?.title ||
    `${t("dashboard", "liveFormEditor.stepPrizes.fallbackTitle", { lng: labelLanguage })} ${index + 1}`;
  const cardTitle =
    value === null
      ? prizeName
      : `${prizeName} (${formatCurrencyAmount({
          value: getDollarValue(value),
          locale: fieldLocale ? getIsoLocaleFromLocale(fieldLocale) : isoLocale,
          currency: organizationCurrency,
        })})`;

  return (
    <TranslationCard title={cardTitle}>
      <input
        style={{ display: "none" }}
        {...register(`${column}.prizesFields.${index}.ticketingPrizeId`)}
        value={ticketingPrizeId}
      />
      {!isTranslationColumn && (
        <>
          <DashboardTextField
            label={titleLabel}
            rules={[isRequired(t("common", "isRequired"))]}
            control={control}
            name={titleName}
            defaultValue={savedTexts?.title}
            errorMessage={titleError}
            data-test="ticketing-prize-title"
          />
          <DashboardRichTextField
            key={swapVersion}
            label={descriptionLabel}
            control={control}
            name={descriptionName}
            defaultValue={descriptionDefaultValue || undefined}
            data-test="ticketing-prize-description"
          />
        </>
      )}
      {isTranslationColumn && isLoading && (
        <DashboardSkeleton variant="rounded" width="100%" sx={(theme) => ({ height: theme.spacing(22) })} />
      )}
      {isTranslationColumn && !isLoading && (
        <>
          <TranslatedTextField
            fieldName={titleName}
            defaultFieldValue={textsToTranslate?.title}
            defaultTranslationFieldValue={savedTexts?.title}
            setValue={setValue}
            translationLocale={translationLocale}
            label={titleLabel}
            rules={[isRequired(t("common", "isRequired"))]}
            control={control}
            errorMessage={titleError}
            dataTest="ticketing-prize-title"
            skipTranslation
          />
          <TranslatedRichTextField
            key={swapVersion}
            fieldName={descriptionName}
            defaultFieldValue={textsToTranslate?.description}
            defaultTranslationFieldValue={descriptionDefaultValue}
            setValue={setValue}
            translationLocale={translationLocale}
            label={descriptionLabel}
            control={control}
            errorMessage={undefined}
            dataTest="ticketing-prize-description"
            skipTranslation
          />
        </>
      )}
    </TranslationCard>
  );
};
